import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Condition } from '@brickloop/core/schemas';
import { useState } from 'react';
import { api } from '../../api';
import { CameraScan, type ScanPick } from '../../components/CameraScan';
import { ExistingBinAlert } from '../../components/ExistingBinAlert';
import { ColorSwatch, ItemImage } from '../../components/ItemImage';
import { useColors, useLocations } from '../../queries';
import { Button, ErrorMessage, Input, Select, Spinner, cn } from '../../ui';

interface Destination {
  /** Null means "no purchase": stock goes in as opening stock, at no cost. */
  batchId: number | null;
  label: string;
}
interface RecentBatch {
  id: number;
  name: string;
  purchaseId: number;
  description: string;
  purchasedOn: string;
}

const NONE: Destination = { batchId: null, label: 'No purchase (free stock)' };
const KEY = 'bl_m_dest';
const load = (): Destination => {
  try {
    return (JSON.parse(sessionStorage.getItem(KEY) ?? 'null') as Destination | null) ?? NONE;
  } catch {
    return NONE;
  }
};

/** Photograph a part, confirm what it is, say how many and where it goes, and it is in stock. */
export function MobileScan() {
  const qc = useQueryClient();
  const colors = useColors();
  const locations = useLocations();
  const [dest, setDest] = useState<Destination>(load);
  const [choosing, setChoosing] = useState(false);
  const [pick, setPick] = useState<ScanPick | null>(null);
  const [condition, setCondition] = useState<Condition>('U');
  const [qty, setQty] = useState(1);
  const [locationId, setLocationId] = useState('');
  const [log, setLog] = useState<string[]>([]);

  const recent = useQuery({
    queryKey: ['recent-batches'],
    enabled: choosing,
    queryFn: () => api<{ batches: RecentBatch[] }>('/api/v1/batches').then((r) => r.batches),
  });
  const choose = (d: Destination) => {
    setDest(d);
    setChoosing(false);
    try {
      sessionStorage.setItem(KEY, JSON.stringify(d));
    } catch {
      /* the choice just is not remembered */
    }
  };

  // Sets and minifigures have no colour: they use colour 0, "not applicable".
  const colour = pick ? (pick.colorName ? colors.data?.find((c) => c.name === pick.colorName) : colors.data?.find((c) => c.id === 0)) ?? null : null;

  const add = useMutation({
    mutationFn: async () => {
      const p = pick!;
      const batchId = dest.batchId ?? (await api<{ batchId: number }>('/api/v1/opening-stock', { method: 'POST' })).batchId;
      return api(`/api/v1/batches/${batchId}/stock`, {
        body: {
          rows: [
            {
              itemType: p.itemType,
              itemNo: p.itemNo,
              itemName: p.itemName,
              colorId: colour?.id ?? 0,
              condition,
              qty,
              locationId: locationId ? Number(locationId) : null,
              price: null,
            },
          ],
        },
      });
    },
    onSuccess: () => {
      const bin = locations.data?.find((l) => String(l.id) === locationId)?.code;
      setLog((l) => [`${qty} × ${pick!.itemNo}${colour && colour.id !== 0 ? ` ${colour.name}` : ''}${bin ? ` → ${bin}` : ''}`, ...l].slice(0, 5));
      setPick(null);
      setQty(1);
      setLocationId('');
      for (const k of ['lots', 'dashboard', 'purchase', 'purchases', 'locations', 'put-away', 'lots-attention', 'lot-lookup']) void qc.invalidateQueries({ queryKey: [k] });
    },
  });

  if (colors.isPending) return <Spinner />;

  return (
    <div className="space-y-4">
      <button type="button" onClick={() => setChoosing((v) => !v)} className="flex min-h-12 w-full items-center justify-between rounded-xl bg-white px-4 text-left shadow-sm ring-1 ring-slate-200">
        <span>
          <span className="block text-xs text-slate-500">Adding to</span>
          <span className="font-medium">{dest.label}</span>
        </span>
        <span className="text-sm text-sky-700">Change</span>
      </button>
      {choosing && (
        <div className="space-y-2 rounded-xl bg-white p-3 shadow-sm ring-1 ring-slate-200">
          <Button large variant="secondary" className="w-full justify-start text-left" onClick={() => choose(NONE)}>
            {NONE.label}
          </Button>
          {recent.isPending && <Spinner />}
          {recent.data?.map((b) => (
            <Button key={b.id} large variant="secondary" className="w-full justify-start text-left" onClick={() => choose({ batchId: b.id, label: `${b.description || `Purchase ${b.purchaseId}`} · ${b.name}` })}>
              <span className="min-w-0 truncate">
                {b.description || `Purchase ${b.purchaseId}`} · {b.name}
                <span className="block text-xs font-normal opacity-70">{b.purchasedOn}</span>
              </span>
            </Button>
          ))}
        </div>
      )}

      {!pick && <CameraScan identifyOnly colors={colors.data ?? []} onAdd={(p) => { setPick(p); setQty(Math.max(1, p.qty)); }} />}

      {pick && (
        <div className="space-y-4 rounded-xl bg-white p-4 shadow-sm ring-1 ring-slate-200">
          <div className="flex gap-3">
            <ItemImage className="size-20" itemType={pick.itemType} itemNo={pick.itemNo} colorId={colour?.id ?? 0} eager />
            <div className="min-w-0">
              <div className="text-lg font-semibold">{pick.itemNo}</div>
              <div className="text-sm text-slate-600">{pick.displayName}</div>
              {colour && colour.id !== 0 && (
                <div className="mt-1 text-sm">
                  <ColorSwatch rgb={colour.rgb} name={colour.name} />
                </div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2" role="radiogroup" aria-label="Condition">
            {(['N', 'U'] as const).map((c) => (
              <button key={c} type="button" role="radio" aria-checked={condition === c} onClick={() => setCondition(c)} className={cn('min-h-12 rounded-lg text-base font-medium ring-1', condition === c ? 'bg-slate-900 text-white ring-slate-900' : 'bg-white ring-slate-300')}>
                {c === 'N' ? 'New' : 'Used'}
              </button>
            ))}
          </div>

          <div className="flex items-center justify-between gap-3">
            <Button large variant="secondary" aria-label="One fewer" disabled={qty <= 1} onClick={() => setQty(qty - 1)}>
              −
            </Button>
            <Input aria-label="Quantity" inputMode="numeric" className="min-h-12 w-24 text-center text-2xl font-semibold" value={qty} onChange={(e) => setQty(Math.max(1, Math.floor(Number(e.target.value.replace(/\D/g, ''))) || 1))} />
            <Button large variant="secondary" aria-label="One more" onClick={() => setQty(qty + 1)}>
              +
            </Button>
          </div>

          <div className="space-y-2 text-sm">
            <ExistingBinAlert itemType={pick.itemType} itemNo={pick.itemNo} colorId={colour?.id} condition={condition} chosen={locationId} onUse={(id) => setLocationId(String(id))} />
            <Select aria-label="Bin" className="min-h-12 w-full text-base" value={locationId} onChange={(e) => setLocationId(e.target.value)}>
              <option value="">No bin yet (put away later)</option>
              {locations.data?.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.code}
                </option>
              ))}
            </Select>
          </div>

          <ErrorMessage error={add.error} />
          <Button large className="w-full" disabled={add.isPending || !colour} onClick={() => add.mutate()}>
            {add.isPending ? 'Adding…' : `Add ${qty} to stock`}
          </Button>
          {!colour && <p className="text-xs text-red-700">That colour is not in your catalogue. Cancel and choose another.</p>}
          <Button large variant="ghost" className="w-full" onClick={() => setPick(null)}>
            Cancel
          </Button>
        </div>
      )}

      {log.length > 0 && (
        <div className="text-sm text-slate-600">
          <div className="mb-1 font-medium text-slate-800">Added this session</div>
          <ul className="space-y-0.5">
            {log.map((l, i) => (
              <li key={i}>✓ {l}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
